/**
 * Sequence Template Gallery
 * A beautiful modal gallery to select pre-built email sequence templates
 * Based on design patterns from ChatbotTemplateSelector
 */

import React, { useState, useMemo } from "react";
import {
  Modal,
  Row,
  Col,
  Card,
  Typography,
  Space,
  Tag,
  Input,
  Button,
  Badge,
  Tooltip,
  Empty,
  Divider,
} from "antd";
import {
  SearchOutlined,
  RocketOutlined,
  HeartOutlined,
  ThunderboltOutlined,
  TrophyOutlined,
  ClockCircleOutlined,
  CrownOutlined,
  MailOutlined,
  CheckCircleOutlined,
  StarOutlined,
  BulbOutlined,
  MessageOutlined,
  GiftOutlined,
  WarningOutlined,
  ReloadOutlined,
  CreditCardOutlined,
  AppstoreOutlined,
  DollarOutlined,
  RiseOutlined,
  CalendarOutlined,
  TeamOutlined,
  ShareAltOutlined,
  UsergroupAddOutlined,
  FileTextOutlined,
  VideoCameraOutlined,
  LineChartOutlined,
  SyncOutlined,
  AlertOutlined,
  AppstoreAddOutlined,
} from "@ant-design/icons";
import {
  ALL_SEQUENCE_TEMPLATES,
  SEQUENCE_TEMPLATE_CATEGORIES,
} from "../sequenceTemplates";

const { Title, Text, Paragraph } = Typography;

// Icon mapping for dynamic icon rendering
const ICON_MAP = {
  RocketOutlined: RocketOutlined,
  HeartOutlined: HeartOutlined,
  ThunderboltOutlined: ThunderboltOutlined,
  TrophyOutlined: TrophyOutlined,
  ClockCircleOutlined: ClockCircleOutlined,
  CrownOutlined: CrownOutlined,
  MailOutlined: MailOutlined,
  CheckCircleOutlined: CheckCircleOutlined,
  StarOutlined: StarOutlined,
  BulbOutlined: BulbOutlined,
  MessageOutlined: MessageOutlined,
  GiftOutlined: GiftOutlined,
  WarningOutlined: WarningOutlined,
  ReloadOutlined: ReloadOutlined,
  CreditCardOutlined: CreditCardOutlined,
  DollarOutlined: DollarOutlined,
  RiseOutlined: RiseOutlined,
  CalendarOutlined: CalendarOutlined,
  TeamOutlined: TeamOutlined,
  ShareAltOutlined: ShareAltOutlined,
  UsergroupAddOutlined: UsergroupAddOutlined,
  FileTextOutlined: FileTextOutlined,
  VideoCameraOutlined: VideoCameraOutlined,
  LineChartOutlined: LineChartOutlined,
  SyncOutlined: SyncOutlined,
  AlertOutlined: AlertOutlined,
  AppstoreAddOutlined: AppstoreAddOutlined,
  AppstoreOutlined: AppstoreOutlined,
};

// Difficulty badge colors
const DIFFICULTY_COLORS = {
  beginner: { bg: "#dcfce7", text: "#15803d", border: "#86efac" },
  intermediate: { bg: "#fef3c7", text: "#b45309", border: "#fcd34d" },
  advanced: { bg: "#fee2e2", text: "#b91c1c", border: "#fca5a5" },
};

// Category type colors
const TYPE_COLORS = {
  onboarding: "#0ea5e9",
  retention: "#ef4444",
  engagement: "#10b981",
  milestone: "#f59e0b",
  inactivity: "#6366f1",
  revenue: "#8b5cf6",
  growth: "#ec4899",
  education: "#f97316",
  lifecycle: "#06b6d4",
};

const SequenceTemplateGallery = ({ visible, onClose, onSelect }) => {
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  // Filter templates based on category and search
  const filteredTemplates = useMemo(() => {
    return ALL_SEQUENCE_TEMPLATES.filter((template) => {
      const matchesCategory =
        selectedCategory === "all" || template.type === selectedCategory;
      const matchesSearch =
        searchQuery === "" ||
        template.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        template.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        template.tags.some((tag) =>
          tag.toLowerCase().includes(searchQuery.toLowerCase())
        );
      return matchesCategory && matchesSearch;
    });
  }, [selectedCategory, searchQuery]);

  // Render icon component dynamically
  const renderIcon = (iconName, style = {}) => {
    const IconComponent = ICON_MAP[iconName];
    return IconComponent ? <IconComponent style={style} /> : null;
  };

  // Handle template selection
  const handleTemplateClick = (template) => {
    setSelectedTemplate(template);
  };

  // Handle use template
  const handleUseTemplate = () => {
    if (selectedTemplate) {
      onSelect(selectedTemplate);
      setSelectedTemplate(null);
      setSelectedCategory("all");
      setSearchQuery("");
      onClose();
    }
  };

  // Template Card Component
  const TemplateCard = ({ template }) => {
    const isSelected = selectedTemplate?.id === template.id;
    const difficultyStyle = DIFFICULTY_COLORS[template.difficulty];
    const typeColor = TYPE_COLORS[template.type] || "#6b7280";

    return (
      <Card
        hoverable
        onClick={() => handleTemplateClick(template)}
        style={{
          height: "100%",
          border: isSelected ? `2px solid ${typeColor}` : "1px solid #e5e7eb",
          borderRadius: 12,
          transition: "all 0.2s ease",
          boxShadow: isSelected
            ? `0 4px 12px ${typeColor}33`
            : "0 1px 3px rgba(0,0,0,0.08)",
          background: isSelected ? `${typeColor}08` : "#fff",
        }}
        bodyStyle={{ padding: 16 }}
      >
        {/* Header */}
        <div style={{ display: "flex", alignItems: "flex-start", gap: 12, marginBottom: 12 }}>
          <div
            style={{
              width: 48,
              height: 48,
              borderRadius: 10,
              background: `linear-gradient(135deg, ${typeColor}20, ${typeColor}40)`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            {renderIcon(template.icon, { fontSize: 24, color: typeColor })}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <Text
              strong
              style={{
                fontSize: 15,
                display: "block",
                lineHeight: 1.3,
                marginBottom: 4,
              }}
            >
              {template.name}
            </Text>
            <Space size={4}>
              <Tag
                style={{
                  borderRadius: 4,
                  border: `1px solid ${typeColor}40`,
                  background: `${typeColor}15`,
                  color: typeColor,
                  fontSize: 11,
                  padding: "0 6px",
                  margin: 0,
                }}
              >
                {template.type}
              </Tag>
              <Tag
                style={{
                  borderRadius: 4,
                  border: `1px solid ${difficultyStyle.border}`,
                  background: difficultyStyle.bg,
                  color: difficultyStyle.text,
                  fontSize: 11,
                  padding: "0 6px",
                  margin: 0,
                }}
              >
                {template.difficulty}
              </Tag>
            </Space>
          </div>
        </div>

        {/* Description */}
        <Paragraph
          ellipsis={{ rows: 2 }}
          style={{
            fontSize: 13,
            color: "#6b7280",
            marginBottom: 12,
            lineHeight: 1.5,
          }}
        >
          {template.description}
        </Paragraph>

        {/* Stats */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "8px 0",
            borderTop: "1px solid #f3f4f6",
          }}
        >
          <Space size={16}>
            <Tooltip title="Emails in sequence">
              <Space size={4}>
                <MailOutlined style={{ color: "#9ca3af", fontSize: 14 }} />
                <Text style={{ fontSize: 13, color: "#6b7280" }}>
                  {template.emails.length} emails
                </Text>
              </Space>
            </Tooltip>
            <Tooltip title="Setup time">
              <Space size={4}>
                <ClockCircleOutlined style={{ color: "#9ca3af", fontSize: 14 }} />
                <Text style={{ fontSize: 13, color: "#6b7280" }}>
                  {template.setupTime}
                </Text>
              </Space>
            </Tooltip>
          </Space>
        </div>

        {/* Selection indicator */}
        {isSelected && (
          <div
            style={{
              position: "absolute",
              top: 8,
              right: 8,
              width: 24,
              height: 24,
              borderRadius: "50%",
              background: typeColor,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CheckCircleOutlined style={{ color: "#fff", fontSize: 14 }} />
          </div>
        )}
      </Card>
    );
  };

  // Template Detail Panel
  const TemplateDetailPanel = ({ template }) => {
    if (!template) {
      return (
        <div
          style={{
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 32,
            textAlign: "center",
          }}
        >
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <div>
                <Text style={{ color: "#9ca3af", display: "block", marginBottom: 8 }}>
                  Select a template to view details
                </Text>
                <Text style={{ fontSize: 12, color: "#d1d5db" }}>
                  Choose from {ALL_SEQUENCE_TEMPLATES.length}+ pre-built sequences based on best practices
                </Text>
              </div>
            }
          />
        </div>
      );
    }

    const typeColor = TYPE_COLORS[template.type] || "#6b7280";

    return (
      <div style={{ padding: 24, height: "100%", overflow: "auto" }}>
        {/* Header */}
        <div style={{ marginBottom: 24 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 12 }}>
            <div
              style={{
                width: 56,
                height: 56,
                borderRadius: 12,
                background: `linear-gradient(135deg, ${typeColor}20, ${typeColor}40)`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {renderIcon(template.icon, { fontSize: 28, color: typeColor })}
            </div>
            <div>
              <Title level={4} style={{ margin: 0, marginBottom: 4 }}>
                {template.name}
              </Title>
              <Space>
                <Tag color={typeColor}>{template.type}</Tag>
                <Tag>{template.difficulty}</Tag>
              </Space>
            </div>
          </div>
          <Paragraph style={{ color: "#6b7280", marginBottom: 0 }}>
            {template.description}
          </Paragraph>
        </div>

        {/* Strategy */}
        <div style={{ marginBottom: 20 }}>
          <Text strong style={{ display: "block", marginBottom: 8, color: "#374151" }}>
            Strategy
          </Text>
          <div
            style={{
              background: "#f9fafb",
              border: "1px solid #e5e7eb",
              borderRadius: 8,
              padding: 12,
            }}
          >
            <Text style={{ fontSize: 13, color: "#4b5563", lineHeight: 1.6 }}>
              {template.strategy}
            </Text>
          </div>
        </div>

        {/* Expected Outcomes */}
        <div style={{ marginBottom: 20 }}>
          <Text strong style={{ display: "block", marginBottom: 8, color: "#374151" }}>
            Expected Outcomes
          </Text>
          <Space direction="vertical" size={4} style={{ width: "100%" }}>
            {template.expectedOutcomes.map((outcome, index) => (
              <div key={index} style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                <CheckCircleOutlined style={{ color: "#10b981", marginTop: 3, fontSize: 14 }} />
                <Text style={{ fontSize: 13, color: "#4b5563" }}>{outcome}</Text>
              </div>
            ))}
          </Space>
        </div>

        {/* Best Practices */}
        <div style={{ marginBottom: 20 }}>
          <Text strong style={{ display: "block", marginBottom: 8, color: "#374151" }}>
            Best Practices
          </Text>
          <Space direction="vertical" size={4} style={{ width: "100%" }}>
            {template.bestPractices.map((practice, index) => (
              <div key={index} style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                <StarOutlined style={{ color: "#f59e0b", marginTop: 3, fontSize: 14 }} />
                <Text style={{ fontSize: 13, color: "#4b5563" }}>{practice}</Text>
              </div>
            ))}
          </Space>
        </div>

        {/* Email Sequence Preview */}
        <div style={{ marginBottom: 20 }}>
          <Text strong style={{ display: "block", marginBottom: 8, color: "#374151" }}>
            Email Sequence ({template.emails.length} emails)
          </Text>
          <Space direction="vertical" size={8} style={{ width: "100%" }}>
            {template.emails.map((email, index) => (
              <div
                key={index}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  padding: 12,
                  background: "#f9fafb",
                  border: "1px solid #e5e7eb",
                  borderRadius: 8,
                }}
              >
                <div
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: "50%",
                    background: typeColor,
                    color: "#fff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 12,
                    fontWeight: 600,
                    flexShrink: 0,
                  }}
                >
                  {index + 1}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <Text strong style={{ fontSize: 13, display: "block" }}>
                    {email.name}
                  </Text>
                  <Text
                    style={{ fontSize: 12, color: "#6b7280" }}
                    ellipsis={{ tooltip: email.subject }}
                  >
                    Subject: {email.subject}
                  </Text>
                </div>
                <Tag style={{ margin: 0, fontSize: 11 }}>
                  +{email.delayAfterPrevious.value} {email.delayAfterPrevious.unit}
                </Tag>
              </div>
            ))}
          </Space>
        </div>

        {/* Tags */}
        <div style={{ marginBottom: 20 }}>
          <Text strong style={{ display: "block", marginBottom: 8, color: "#374151" }}>
            Tags
          </Text>
          <Space wrap size={4}>
            {template.tags.map((tag) => (
              <Tag key={tag} style={{ borderRadius: 4 }}>
                {tag}
              </Tag>
            ))}
          </Space>
        </div>

        {/* Best For */}
        <div>
          <Text strong style={{ display: "block", marginBottom: 8, color: "#374151" }}>
            Best For
          </Text>
          <Space wrap size={4}>
            {template.bestFor.map((item) => (
              <Tag key={item} color="blue" style={{ borderRadius: 4 }}>
                {item}
              </Tag>
            ))}
          </Space>
        </div>
      </div>
    );
  };

  return (
    <Modal
      title={null}
      visible={visible}
      onCancel={onClose}
      footer={null}
      width={1200}
      bodyStyle={{ padding: 0, height: "90vh", overflow: "hidden" }}
      style={{ top: 20 }}
      destroyOnClose
    >
      <div style={{ display: "flex", height: "100%" }}>
        {/* Left Panel - Template List */}
        <div
          style={{
            width: "60%",
            borderRight: "1px solid #e5e7eb",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Header */}
          <div style={{ padding: 20, borderBottom: "1px solid #e5e7eb" }}>
            <Title level={4} style={{ margin: 0, marginBottom: 4 }}>
              Choose a Sequence Template
            </Title>
            <Text style={{ color: "#6b7280" }}>
              {ALL_SEQUENCE_TEMPLATES.length}+ pre-built sequences based on best practices from billion-dollar companies
            </Text>
          </div>

          {/* Filters */}
          <div style={{ padding: "16px 20px", borderBottom: "1px solid #f3f4f6" }}>
            {/* Category Pills */}
            <Space wrap size={8} style={{ marginBottom: 16 }}>
              <Button
                type={selectedCategory === "all" ? "primary" : "default"}
                size="small"
                onClick={() => setSelectedCategory("all")}
                icon={<AppstoreOutlined />}
                style={{ borderRadius: 6 }}
              >
                All ({ALL_SEQUENCE_TEMPLATES.length})
              </Button>
              {SEQUENCE_TEMPLATE_CATEGORIES.map((category) => {
                const CategoryIcon = ICON_MAP[category.icon];
                return (
                  <Button
                    key={category.id}
                    type={selectedCategory === category.id ? "primary" : "default"}
                    size="small"
                    onClick={() => setSelectedCategory(category.id)}
                    icon={CategoryIcon ? <CategoryIcon /> : null}
                    style={{
                      borderRadius: 6,
                      borderColor:
                        selectedCategory === category.id ? category.color : undefined,
                      background:
                        selectedCategory === category.id ? category.color : undefined,
                    }}
                  >
                    {category.name} ({category.templates.length})
                  </Button>
                );
              })}
            </Space>

            {/* Search */}
            <Input
              placeholder="Search templates..."
              prefix={<SearchOutlined style={{ color: "#9ca3af" }} />}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              allowClear
              style={{ borderRadius: 8 }}
            />
          </div>

          {/* Template Grid */}
          <div style={{ flex: 1, overflow: "auto", padding: 20 }}>
            {filteredTemplates.length > 0 ? (
              <Row gutter={[16, 16]}>
                {filteredTemplates.map((template) => (
                  <Col key={template.id} xs={24} sm={12}>
                    <TemplateCard template={template} />
                  </Col>
                ))}
              </Row>
            ) : (
              <Empty
                description="No templates found"
                style={{ marginTop: 60 }}
              />
            )}
          </div>
        </div>

        {/* Right Panel - Template Details */}
        <div
          style={{
            width: "40%",
            display: "flex",
            flexDirection: "column",
            background: "#fff",
          }}
        >
          <div style={{ flex: 1, overflow: "auto" }}>
            <TemplateDetailPanel template={selectedTemplate} />
          </div>

          {/* Action Footer */}
          <div
            style={{
              padding: 16,
              borderTop: "1px solid #e5e7eb",
              display: "flex",
              justifyContent: "flex-end",
              gap: 12,
            }}
          >
            <Button onClick={onClose}>Cancel</Button>
            <Button
              type="primary"
              disabled={!selectedTemplate}
              onClick={handleUseTemplate}
              style={{
                background: selectedTemplate
                  ? TYPE_COLORS[selectedTemplate.type]
                  : undefined,
                borderColor: selectedTemplate
                  ? TYPE_COLORS[selectedTemplate.type]
                  : undefined,
              }}
            >
              Use This Template
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default SequenceTemplateGallery;
